import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import cloneDeep from 'lodash/cloneDeep';
import {
  AngebotsId,
  determineTkvV2Produkttabelle,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  ProduktDeleted,
  ProdukttabelleEnum,
  SnapshotManager,
} from '@ps20-frontend/shop-integration-redux';
import { App, TierartEnum } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { IAppState } from '../appState';
import { ChildState } from '../mappers/childStates';
import { childKeyOf, childOfKey, listedChildKeys } from '../models/childKeys';
import { animalsWithoutDeletedChildren } from '../models/deletedOffers';
import { derivePositions } from '../models/tierPositions';
import { TgvActions } from '../tgvActions';
import { removeOrphanedOffersAndKeys } from './orphanedOffersSaga';
import { dispatchToStoreOf, findStoreState } from './sharedDispatcher';

const targetsTgvState = (payload: AngebotsId, state: IAppState): boolean =>
  payload.appId === App.TGV && payload.angebotsId === state.angebotsId;

const listedAndSettledKeys = ({ tiergruppe, transientState }: IAppState): string[] =>
  listedChildKeys(tiergruppe).filter(key => !transientState.children.pendingDeletion.includes(key));

const captureChildren = (keys: string[]): Map<string, ChildState> => {
  const captures = new Map<string, ChildState>();
  for (const key of keys) {
    const { appId, angebotsId } = childOfKey(key);
    const childState = findStoreState(appId, angebotsId);
    if (childState !== undefined) {
      captures.set(key, cloneDeep(childState));
    }
  }
  return captures;
};

const TKV_COVERAGE_VOLLSCHUTZ = 'vollschutz';

interface ProductReload {
  expectedProdukttabelle?: ProdukttabelleEnum | null;
  productLoadingIsInProgress?: boolean;
}

const productReloadOf = (state: ChildState | undefined): ProductReload | null | undefined =>
  state?.['transientState'] as ProductReload | null | undefined;

const runsProductReload = (reload: ProductReload | null | undefined): boolean =>
  reload?.expectedProdukttabelle != null || reload?.productLoadingIsInProgress === true;

const productTableOf = (state: ChildState): ProdukttabelleEnum =>
  determineTkvV2Produkttabelle(
    (state.versicherungsnehmer as { animalType?: TierartEnum } | undefined)?.animalType,
    (state['vertragsoptionen'] as { coverageType?: string } | undefined)?.coverageType ===
      TKV_COVERAGE_VOLLSCHUTZ,
  );

const withLiveProductReload = (captured: ChildState, live: ChildState | undefined): ChildState => {
  const capturedReload = productReloadOf(captured);
  if (capturedReload == null) {
    return captured;
  }
  if (runsProductReload(productReloadOf(live))) {
    return {
      ...captured,
      transientState: { ...capturedReload, expectedProdukttabelle: productTableOf(captured) },
    };
  }
  if (runsProductReload(capturedReload)) {
    return {
      ...captured,
      transientState: {
        ...capturedReload,
        expectedProdukttabelle: undefined,
        productLoadingIsInProgress: false,
      },
    };
  }
  return captured;
};

const restoreChild = (key: string, captured: ChildState): boolean => {
  const { appId, angebotsId } = childOfKey(key);
  const globalActions = new GlobalActions();
  const copy = cloneDeep(captured);
  const restored = dispatchToStoreOf(
    appId,
    angebotsId,
    globalActions.replaceState(
      appId === App.TKV_V2 ? withLiveProductReload(copy, findStoreState(appId, angebotsId)) : copy,
      appId,
      angebotsId,
    ),
  );
  if (restored) {
    dispatchToStoreOf(
      appId,
      angebotsId,
      globalActions.createStreckeSnapshot({ appId, angebotsId }),
    );
  }
  return restored;
};

const withoutDeletedChildren = (
  snapshot: IAppState,
  deletedKeys: ReadonlySet<string>,
): IAppState => {
  const animals = animalsWithoutDeletedChildren(snapshot.tiergruppe, deletedKeys);
  return animals === snapshot.tiergruppe.tiere
    ? snapshot
    : {
        ...snapshot,
        tiergruppe: { ...snapshot.tiergruppe, tiere: animals, positions: derivePositions(animals) },
      };
};

export class TgvSnapshotManager extends SnapshotManager<IAppState> {
  private captures = new Map<string, ChildState>();
  private sentTakeOvers = new Set<string>();
  private capturedChildDeleted = false;
  private deletedSinceCapture = new Set<string>();
  private unlinkedSinceCapture = new Set<string>();
  private visitStartAngebotsId: string | undefined;

  constructor() {
    super(App.TGV);
  }

  *createSnapshot(action: GlobalAction<AngebotsId>): SagaIterator {
    yield* super.createSnapshot(action);
    const state: IAppState = yield select();
    if (targetsTgvState(action.payload, state)) {
      this.captures = captureChildren(listedAndSettledKeys(state));
      this.sentTakeOvers.clear();
      this.capturedChildDeleted = false;
      this.deletedSinceCapture.clear();
      this.unlinkedSinceCapture.clear();
      this.visitStartAngebotsId = state.angebotsId;
    }
  }

  *restoreSnapshot(action: GlobalAction<AngebotsId>): SagaIterator {
    const { payload } = action;
    const state: IAppState = yield select();
    const hasSnapshot = this.snapshots.some(snapshot => snapshot.angebotsId === payload.angebotsId);

    const targetsOwnState = targetsTgvState(payload, state);

    if (targetsOwnState && !hasSnapshot) {
      yield call(
        GlobalPut.getInstance().put,
        new GlobalActions().logError(App.TGV, 'Snapshot ist undefined.'),
      );
      return;
    }

    const restoredKeys = targetsOwnState ? this.restoreChildren(listedAndSettledKeys(state)) : [];
    if (targetsOwnState) {
      this.dropDeletedChildren(payload.angebotsId);
    }
    yield* super.restoreSnapshot(action);
    if (targetsOwnState) {
      const deletedOffers: boolean = yield call(
        removeOrphanedOffersAndKeys,
        this.keysCreatedSinceCapture(state),
      );
      const unfinishedKeys = restoredKeys.filter(key => this.sentTakeOvers.has(key));
      this.sentTakeOvers.clear();
      if (unfinishedKeys.length > 0) {
        yield put(
          new TgvActions().takeOverAgainRequested(
            unfinishedKeys,
            this.capturedChildDeleted && !deletedOffers,
          ),
        );
      }
    }
  }

  restoreChildren(keys: string[]): string[] {
    const restoredKeys: string[] = [];
    for (const key of keys) {
      const captured = this.captures.get(key);
      if (captured !== undefined && restoreChild(key, captured)) {
        restoredKeys.push(key);
      }
    }
    return restoredKeys;
  }

  restoreOwnState(): void {
    this.dropDeletedChildren(this.visitStartAngebotsId);
    const snapshot = this.snapshots.find(it => it.angebotsId === this.visitStartAngebotsId);
    if (snapshot !== undefined) {
      GlobalPut.getInstance().put(
        new GlobalActions().replaceState(snapshot, App.TGV, this.visitStartAngebotsId),
      );
    }
  }

  markTakeOverSent(key: string): void {
    this.sentTakeOvers.add(key);
  }

  markTakeOversSettled(): void {
    this.sentTakeOvers.clear();
  }

  rememberUnlinkedChildren(keys: string[]): void {
    for (const key of keys) {
      this.unlinkedSinceCapture.add(key);
    }
  }

  forgetChildren(keys: string[]): void {
    for (const key of keys) {
      if (!this.unlinkedSinceCapture.has(key)) {
        this.deletedSinceCapture.add(key);
      }
      if (this.captures.delete(key)) {
        this.capturedChildDeleted = true;
      }
    }
  }

  private dropDeletedChildren(angebotsId: string | undefined): void {
    this.snapshots = this.snapshots.map(snapshot =>
      snapshot.angebotsId === angebotsId
        ? withoutDeletedChildren(snapshot, this.deletedSinceCapture)
        : snapshot,
    );
  }

  private keysCreatedSinceCapture({ tiergruppe }: IAppState): string[] {
    return listedChildKeys(tiergruppe).filter(key => !this.captures.has(key));
  }
}

export const snapshotManager = new TgvSnapshotManager();

const forgetDeletedChildren = (action: GlobalAction<ProduktDeleted>): void => {
  snapshotManager.forgetChildren(
    (action.payload?.angebotsIds ?? [])
      .map(childKeyOf)
      .filter((key): key is string => key !== null),
  );
};

export function* watchStreckeSnapshot() {
  yield takeLatest(
    GlobalActions.CREATE_STRECKEN_SNAPSHOT,
    safeSaga(snapshotManager.createSnapshot, App.TGV),
  );
  yield takeLatest(
    GlobalActions.RESTORE_STRECKEN_SNAPSHOT,
    safeSaga(snapshotManager.restoreSnapshot, App.TGV),
  );
  yield takeEvery(GlobalActions.PRODUKT_DELETED, safeSaga(forgetDeletedChildren, App.TGV));
}
